import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { Bug, Lightbulb, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FeedbackStatusChip } from "@/components/feedback/FeedbackStatusChip";

const fmt = d => { try { return format(parseISO(d), "d MMM yyyy"); } catch { return d; } };
const empty = { kind: "improvement", title: "", details: "", page: "" };

export default function AppFeedback() {
  const [form, setForm] = useState(empty);
  const [mine, setMine] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/feedback/mine").then(r => setMine(r.data)).catch(() => setMine([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 3) return toast.error("Give it a short title");
    if (form.details.trim().length < 5) return toast.error("Add a few more details");
    setBusy(true);
    try {
      await api.post("/feedback", { ...form, page: form.page || null });
      toast.success(form.kind === "bug" ? "Bug reported — thank you" : "Suggestion sent — thank you");
      setForm(empty); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-8" data-testid="app-feedback-page">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">App settings</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl mt-1">App Improvement Request</h1>
        <p className="opacity-60 text-sm mt-1">Spotted a bug or have an idea to make the app better? Log it here and track its progress.</p>
      </div>

      <form onSubmit={submit} className="card-tactical rounded-md p-5 space-y-4" data-testid="feedback-form">
        <div className="inline-flex rounded border border-border overflow-hidden" data-testid="feedback-kind">
          {[["improvement", "Improvement", Lightbulb], ["bug", "Bug", Bug]].map(([k, l, Icon]) => (
            <button key={k} type="button" onClick={() => setForm({ ...form, kind: k })} data-testid={`feedback-kind-${k}`}
              className={`px-4 py-2 text-[10px] tracking-widest uppercase inline-flex items-center gap-1.5 ${form.kind === k ? "bg-[#83B68B] text-white" : "hover:text-[#83B68B]"}`}><Icon className="w-3 h-3" /> {l}</button>
          ))}
        </div>
        <div>
          <Label className="text-[10px] tracking-widest uppercase opacity-70">Title</Label>
          <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} maxLength={140} data-testid="feedback-title" className="mt-1.5" placeholder={form.kind === "bug" ? "e.g. Calendar won't load on Saturday" : "e.g. Add a printable ref day sheet"} />
        </div>
        <div>
          <Label className="text-[10px] tracking-widest uppercase opacity-70">Details</Label>
          <Textarea rows={5} value={form.details} onChange={e => setForm({ ...form, details: e.target.value })} data-testid="feedback-details" className="mt-1.5"
            placeholder={form.kind === "bug" ? "What did you do, what happened, and what did you expect? Phone or computer?" : "What would you change and why would it help?"} />
        </div>
        <div>
          <Label className="text-[10px] tracking-widest uppercase opacity-70">Where in the app <span className="opacity-50">(optional)</span></Label>
          <Input value={form.page} onChange={e => setForm({ ...form, page: e.target.value })} maxLength={200} data-testid="feedback-page" className="mt-1.5" placeholder="e.g. Calendar, Kit orders, My bookings" />
        </div>
        <div className="flex justify-end">
          <button type="submit" disabled={busy} data-testid="feedback-submit" className="btn-volt px-5 py-2.5 rounded inline-flex items-center gap-2 text-[10px] tracking-widest uppercase disabled:opacity-40"><Send className="w-3 h-3" /> {busy ? "Sending…" : "Send request"}</button>
        </div>
      </form>

      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-3">Your requests</div>
        {mine === null ? <div className="opacity-50 font-display tracking-widest">LOADING…</div>
          : mine.length === 0 ? <div className="card-tactical rounded-md p-8 text-center opacity-60" data-testid="feedback-mine-empty">Nothing logged yet.</div>
          : (
            <div className="space-y-2" data-testid="feedback-mine-list">
              {mine.map(f => (
                <div key={f.id} className="card-tactical rounded-md p-4" data-testid={`feedback-mine-${f.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    {f.kind === "bug" ? <Bug className="w-3.5 h-3.5 text-red-400" /> : <Lightbulb className="w-3.5 h-3.5 text-[#83B68B]" />}
                    <span className="font-semibold">{f.title}</span>
                    <FeedbackStatusChip status={f.status} />
                    <span className="text-xs opacity-50 ml-auto">{fmt(f.created_at)}{f.page ? ` · ${f.page}` : ""}</span>
                  </div>
                  <p className="text-sm opacity-80 mt-2 whitespace-pre-wrap">{f.details}</p>
                  {f.admin_note && <div className="mt-3 text-sm border-l-2 border-[#83B68B] pl-3 opacity-90" data-testid={`feedback-note-${f.id}`}><span className="text-[10px] tracking-widest uppercase text-[#83B68B]">Committee response</span><br />{f.admin_note}</div>}
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}
